import { Box, Button, Card, CardContent, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { tableCellClasses } from "@mui/material/TableCell";
import { fetchIntegrationHistory, parseDateToLocalString } from "../../services/integrationService";
import { useCallback, useEffect, useState } from "react";
import "../integrationDashboard/integrationDashboard.css";
import { integrationStats, integrationSummary } from "../../types/types";
import DownloadIcon from '@mui/icons-material/Download';
import { Loader } from "../../components/loader/Loader";
import { TranslationEnum, usePortalTranslation } from "../../providers/TranslationProvider";
import { Code } from "@mantine/core";

export function IntegrationStatistics() {
    const { translate } = usePortalTranslation();
    const CUR_COMPONENT = "Integration Statistics"
    const CUR_COMPONENT_COMMON="commonPortal";
    const authContext = JSON.parse(localStorage.authContext);
    const [isLoading,setIsLoading] = useState<boolean>(true);
    const [jobSummary, setJobSummary] = useState<integrationSummary[]>([]);

    const fetchHistory = useCallback(async () => {
        setIsLoading(true)
        const response: integrationSummary[] = await fetchIntegrationHistory(authContext.env, authContext.user.lob);
        setJobSummary(response.reverse());
        setIsLoading(false);
    }, [authContext.env, authContext.user.lob])
    const handleBackClick = () => {
        window.history.back();
    };

    useEffect(() => {
        fetchHistory();
    }, []);

    return (
        <Box className={"white-box1"} position={"relative"}>
            {isLoading ? <Loader/> : 
            (<>
                <div  style={{display:"flex",justifyContent:"space-between",width:"100%", flexDirection:"row"}}>
                    <h3 className="integration-dashboard-heading">{translate(TranslationEnum.integration_statistics,"Integration Summary")}</h3>
                    <Button variant="contained" color="primary" onClick={handleBackClick} style={{ color: "white", backgroundColor: "#24c6b1", fontWeight:"bold" }}>
                    {translate(TranslationEnum.common_portal,"BACK")}
             </Button>
                </div>
                <Stack spacing={4} marginTop={5} >
                    {jobSummary.length > 0 ? jobSummary.map((summary, index) => (
                        <Card key={`${summary}-${index}`} className='integration-stats-card'>
                            <StatBox key={`${summary}-${index}`} stats={summary.stats} durationString={`${parseDateToLocalString(summary.startTime)} ${translate(TranslationEnum.integration_statistics,"to")} ${parseDateToLocalString(summary.endTime)}`} downloadLink={`https://d15q3m60ny32k7.cloudfront.net/${authContext.user.lob}_daily_statsout_${summary.dumpFileKey}.zip`}></StatBox>
                        </Card>
                    )): <Box className={"white-box1"}>
                            <Card sx={{ textAlign:"center",backgroundColor: "#f8f9fa !important" }} className='integration-stats-card'>
                                <CardContent sx={{ color: "grey" }}>
                                {translate(TranslationEnum.integration_statistics,"No integration statistics found for the selected environment and lob")}
                                </CardContent>
                            </Card>
                        </Box>}
                </Stack>
            </>)}
        </Box>
    );
}


function StatBox(props: {
    stats: string;
    durationString: string;
    downloadLink: string;
}) {
    const { translate } = usePortalTranslation();
    const CUR_COMPONENT = "Integration Statistics"
    const result: integrationStats[] = JSON.parse(props.stats);

    const calculateErrors = (entityList: integrationStats[]) => {
        const failureCount = entityList.map((entity) => entity.failure).reduce((object, sum) => object + sum, 0);
        return failureCount;
    }

    const downloadZip = () => {
        window.location.replace(props.downloadLink);
    }

    return (
            <CardContent>
                <Box marginBottom={2} display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                    <Typography variant='subtitle2' fontWeight={600}>{translate(TranslationEnum.integration_statistics,"Duration")} : {props.durationString}</Typography>
                        <Typography variant='subtitle2'>{translate(TranslationEnum.integration_statistics,"Failures")} : <b style={{ color: "red" }}>{calculateErrors(result)}</b>
                        <IconButton onClick={downloadZip}>
                            <DownloadIcon></DownloadIcon>
                        </IconButton>
                    </Typography>
                </Box>
                <Table sx={{
                    [`& .${tableCellClasses.root}`]: {
                        borderBottom: "none",
                        justifyContent: "start",
                        textAlign: "start !important"
                    }
                }}>
                    <TableHead sx={{ padding: '0 !important' }}>
                        <TableRow>
                            <TableCell component={"th"} padding="none">
                                <b>{translate(TranslationEnum.integration_statistics,"Data Type")}</b>
                            </TableCell>
                            <TableCell component={"th"} padding="none">
                                <b>{translate(TranslationEnum.integration_statistics,"Success")}</b>
                            </TableCell>
                            <TableCell component={"th"} padding="none">
                                <b>{translate(TranslationEnum.integration_statistics,"Failure")}</b>
                            </TableCell>
                            <TableCell component={"th"} padding="none">
                                <b>{translate(TranslationEnum.integration_statistics,"Server Errors")}</b>
                            </TableCell>
                            <TableCell component={"th"} padding="none">
                                <b>{translate(TranslationEnum.integration_statistics,"Total")}</b>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ padding: '0 !important' }}>
                        {result.length > 0 && result.map((object, index) => (
                            <TableRow key={`${object}-${index}`}>
                                <TableCell component={"th"} padding="none">
                                    {object.entityName}
                                </TableCell>
                                <TableCell component={"th"} padding="none">
                                    {object.success}
                                </TableCell>
                                <TableCell component={"th"} padding="none">
                                    {object.failure}
                                </TableCell>
                                <TableCell component={"th"} padding="none">
                                    {object.serverFailure}
                                </TableCell>
                                <TableCell component={"th"} padding="none">
                                    {object.total}
                                </TableCell>
                            </TableRow>))
                        }
                    </TableBody>
                </Table>
                {result.length === 0 && <Code block className="integration-stats-nodata" spellCheck={false}>No Data Available</Code>}
            </CardContent>
    )
}